import type { PropertyDetailsFragment } from '__generated__/athena/graphql';

export interface GalleryPhoto {
  url: string;
  urlLg?: string | null;
  urlXl?: string | null;
  label?: string | null;
  isFloorPlan: boolean;
}

type GalleryPhotoProperty = Pick<
  PropertyDetailsFragment,
  'photos' | 'photosLg' | 'photosXl' | 'floorPlanPhotos'
>;

// Builds the ordered set of gallery photos for a property by appending any
// available floor plan photos after the standard listing photos. Consumers
// (GalleryPhotos, PhotoCarousel, Lightbox) share this helper so the gallery
// index -> URL mapping stays consistent across surfaces; a click on the Nth
// tile in the gallery opens the same Nth photo in the lightbox.
export const buildGalleryPhotos = (
  property: GalleryPhotoProperty | null | undefined,
): GalleryPhoto[] => {
  const photos = property?.photos ?? [];
  const photosLg = property?.photosLg ?? [];
  const photosXl = property?.photosXl ?? [];
  const listingPhotos: GalleryPhoto[] = photos.map((url, index) => ({
    url,
    urlLg: photosLg[index] ?? null,
    urlXl: photosXl[index] ?? null,
    isFloorPlan: false,
  }));
  const floorPlanPhotos: GalleryPhoto[] =
    property?.floorPlanPhotos
      ?.filter((fp) => !!fp?.url)
      .map((fp) => ({ url: fp.url, label: fp.label ?? null, isFloorPlan: true })) ?? [];
  return [...listingPhotos, ...floorPlanPhotos];
};

// Photos for carousels backed by bricks-next `PhotoCarousel`. Its internal
// `Image` uses `resizeMode: cover` with no per-photo override, so a
// naturally-portrait floor plan gets cover-cropped to fill the square/16:9
// slide regardless of which URL variant we pass. Until bricks-next exposes
// per-photo resize, keep floor plans out of those carousels and surface
// them in the rendering paths we control (desktop GalleryPhotos, mobile
// "see all photos" modal, Lightbox), all of which use resizeMode: contain.
export const buildCarouselPhotos = (
  property: GalleryPhotoProperty | null | undefined,
): GalleryPhoto[] => buildGalleryPhotos(property).filter((p) => !p.isFloorPlan);
